import { type PropsWithChildren, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  BarChartIcon,
  Chat_1Icon,
  Home04Icon,
  NotificationIcon,
  SettingIcon,
} from "vega-icons";

import { useAuth } from "@vega-api/custom/api/hooks/useAuth";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { FrontendPortal } from "@vega-api/generated/types/frontendPortal";
import { MenuItemType } from "@vega-core/components/Deprecated/DropdownMenu/MenuItems.type";
import {
  LayoutContext,
  type LayoutContextProps,
} from "@vega-core/components/Layout/LayoutContext";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { m } from "@vega-core/paraglide/messages";
import { MobileNavigation } from "@vega-modules/components/MobileNavigation/MobileNavigation";
import { SelectOrganisation } from "@vega-modules/components/SelectOrganisation/SelectOrganisation";
import {
  CoInvestmentFullPaths,
  CommonPaths,
  VegaModulesPaths,
} from "@vega-modules/paths";
import { NotificationNavBadge } from "@vega-modules/scenes/CoInvestments/components/NotificationNavBadge/NotificationNavBadge";
import { COINVEST_NOTIFICATION_PUBLISHER } from "@vega-modules/scenes/CoInvestments/constants/notificationConstants";
import { useUnreadMessagesTotal } from "@vega-modules/scenes/CoInvestments/messaging/conversationsData";
import { useScopedUnreadCount } from "@vega-modules/scenes/Notifications/useScopedUnreadCount";

const source = FrontendPortal.COINVEST_INVESTOR_PORTAL;

export function LpCoInvestLayoutContextProvider({
  children,
}: PropsWithChildren) {
  const { isLoading: isAuthLoading } = useAuth();
  const [isNavigationActionLoading, setIsNavigationActionLoading] =
    useState(false);

  const navigationItems = useGetNavigationItems();
  const secondaryMenuItems = useGetSecondaryNavigationItems({
    setIsNavigationActionLoading,
  });

  const sideNav: LayoutContextProps = useMemo(
    () => ({
      navigationItems: navigationItems,
      secondaryMenuItems: secondaryMenuItems,
    }),
    [navigationItems, secondaryMenuItems],
  );

  return (
    <LayoutContext.Provider value={sideNav}>
      <MobileNavigation
        logoutSource={source}
        orgNode={<SelectOrganisation />}
      />
      {isAuthLoading || isNavigationActionLoading ? <LoadingSpinner /> : null}
      {children}
    </LayoutContext.Provider>
  );
}

function useGetNavigationItems() {
  const { featureFlag } = useFeatureFlag();
  const isNotificationsEnabled =
    featureFlag[FeatureFlag.COINVEST_NOTIFICATIONS];
  const isInsightsEnabled = featureFlag[FeatureFlag.COINVEST_INSIGHTS_VD_26155];
  const isMessagingEnabled =
    featureFlag[FeatureFlag.COINVEST_MESSAGING_VD_28584];

  const notificationsCount = useScopedUnreadCount(
    { publisher: COINVEST_NOTIFICATION_PUBLISHER },
    { enabled: isNotificationsEnabled },
  );

  const messagesCount = useUnreadMessagesTotal({
    enabled: isMessagingEnabled,
  });

  const items: LayoutContextProps["navigationItems"] = [];

  items.push({
    id: "co-investments",
    href: CoInvestmentFullPaths.CoInvestments,
    icon: Home04Icon,
    name: m.navTitles_coInvestments(),
    isActive: (pathname: string) =>
      pathname === CommonPaths.Home ||
      (pathname.startsWith(CoInvestmentFullPaths.CoInvestments) &&
        !pathname.startsWith(
          CoInvestmentFullPaths.CoInvestmentLpNotifications,
        ) &&
        !pathname.startsWith(CoInvestmentFullPaths.CoInvestmentLpMessages) &&
        !pathname.startsWith(CoInvestmentFullPaths.CoInvestmentLpInsights)),
    analyticsSelector: "gtm-nav-co-investments",
  });

  if (isInsightsEnabled) {
    items.push({
      id: "co-investments-insights",
      href: CoInvestmentFullPaths.CoInvestmentLpInsights,
      icon: BarChartIcon,
      name: m.coInvestments_nav_insights(),
      analyticsSelector: "gtm-nav-co-investments-insights",
    });
  }

  if (isNotificationsEnabled) {
    items.push({
      id: "co-investments-notifications",
      href: CoInvestmentFullPaths.CoInvestmentLpNotifications,
      icon: NotificationIcon,
      name: m.coInvestments_nav_notifications(),
      rightIcon: ({ isCollapsed }) => (
        <NotificationNavBadge
          count={notificationsCount}
          isCollapsed={isCollapsed}
        />
      ),
      analyticsSelector: "gtm-nav-co-investments-notifications",
    });
  }

  if (isMessagingEnabled) {
    items.push({
      id: "co-investments-messages",
      href: CoInvestmentFullPaths.CoInvestmentLpMessages,
      icon: Chat_1Icon,
      name: m.coInvestments_nav_messages(),
      rightIcon: ({ isCollapsed }) => (
        <NotificationNavBadge count={messagesCount} isCollapsed={isCollapsed} />
      ),
      analyticsSelector: "gtm-nav-co-investments-messages",
    });
  }

  return items;
}

function useGetSecondaryNavigationItems({
  setIsNavigationActionLoading,
}: {
  setIsNavigationActionLoading: (isLoading: boolean) => void;
}) {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const items = [];

  items.push(
    [
      {
        action: async () => navigate(VegaModulesPaths.Settings),
        icon: <SettingIcon />,
        title: m.titles_settings(),
      },
    ],
    [
      {
        action: async () => {
          setIsNavigationActionLoading(true);

          try {
            await logout({
              isGlobal: true,
              savePath: false,
              source,
            });
          } catch (error) {
            console.error(error);
          } finally {
            setIsNavigationActionLoading(false);
          }
        },
        analyticsSelector: "gtm-auth-logout",
        title: m.titles_signOut(),
        type: MenuItemType.Button,
      },
    ],
  );

  return items;
}
